import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { MobileStickyCTA } from "../components/site/MobileStickyCTA";
const FloatingAssistant = lazy(() => import("../components/site/FloatingAssistant").then((m) => ({ default: m.FloatingAssistant })));

function DeferredAssistant() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 2500);
    return () => clearTimeout(t);
  }, []);
  return ready ? <Suspense fallback={null}><FloatingAssistant /></Suspense> : null;
}

function NotFoundComponent() {
  return (
    <>
      <Header />
      <div className="container-x py-24 md:py-32 text-center">
        <span className="eyebrow">404</span>
        <h1 className="mt-4 font-display text-5xl md:text-6xl">Page not found</h1>
        <p className="mt-4 text-muted-foreground max-w-md mx-auto">The page you're looking for isn't here. Head back and we'll help you find what you need.</p>
        <Link to="/" className="btn-primary mt-8">Back to home</Link>
      </div>
      <Footer />
    </>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-2xl">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong. Try again or head home.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button onClick={() => { router.invalidate(); reset(); }} className="btn-primary">Try again</button>
          <a href="/" className="btn-outline">Go home</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "oIwdauHiCG5YDIp4JpUNbPm0EAh1FbM6Akgi_DdNclw" },
      { title: "IZHUB Cleaning Service — Reliable Cleaners in Tidworth, Andover & Salisbury" },
      { name: "description", content: "Professional cleaning across Salisbury Plain. Domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning. Fully insured. Free quote in 2 hours." },
      { name: "author", content: "IZHUB Cleaning Service" },
      { property: "og:title", content: "IZHUB Cleaning Service — Reliable Cleaners in Tidworth, Andover & Salisbury" },
      { property: "og:description", content: "Professional cleaning across Salisbury Plain. Domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning. Fully insured. Free quote in 2 hours." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "IZHUB Cleaning Service" },
      { property: "og:locale", content: "en_GB" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "IZHUB Cleaning Service — Reliable Cleaners in Tidworth, Andover & Salisbury" },
      { name: "twitter:description", content: "Professional cleaning across Salisbury Plain. Domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning. Fully insured. Free quote in 2 hours." },
      { name: "theme-color", content: "#2b4a45" },
      { property: "og:image", content: "https://izhubcleaning.uk/og-image.jpg" },
      { name: "twitter:image", content: "https://izhubcleaning.uk/og-image.jpg" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
    ],

    scripts: [
      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=G-EG0FEHEPYE",
      },
      {
        children:
          "window.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag('js', new Date());\ngtag('config', 'AW-18310525767');\ngtag('config', 'G-EG0FEHEPYE');",
      },
      {
        children:
          "window.addEventListener('load',function(){setTimeout(function(){(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src=\"https://www.clarity.ms/tag/\"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window, document, \"clarity\", \"script\", \"xpdiquxljk\");},3000);});",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://izhubcleaning.uk/#organization",
              name: "IZHUB Cleaning Service",
              url: "https://izhubcleaning.uk",
              logo: "https://izhubcleaning.uk/og-image.jpg",
              email: "info@izhubcleaning.uk",
              telephone: ["+44 7763 933920"],
              sameAs: [
                "https://www.google.com/maps/place/IZHUB+Cleaning+Service/@51.160456,-1.6642579,15z/data=!4m6!3m5!1s0xaa0d05ebd417799d:0x4b6d9fef3026d0a4!8m2!3d51.160456!4d-1.6642579!16s%2Fg%2F11z308wqpq",
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://izhubcleaning.uk/#website",
              url: "https://izhubcleaning.uk",
              name: "IZHUB Cleaning Service",
              publisher: { "@id": "https://izhubcleaning.uk/#organization" },
              inLanguage: "en-GB",
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-dvh flex-col">
        <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-full focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground">Skip to content</a>
        <Header />
        <main id="main" className="flex-1">
          <Outlet />
        </main>
        <Footer />
        <MobileStickyCTA />
        <DeferredAssistant />
      </div>
    </QueryClientProvider>
  );
}
